"use client";

import { forwardRef, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";

export interface TabsScrollerProps {
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  showArrows?: boolean;
  snap?: boolean;
  ariaLabelPrev?: string;
  ariaLabelNext?: string;
}

export const TabsScroller = forwardRef<HTMLDivElement, TabsScrollerProps>(
  (
    {
      children,
      className,
      contentClassName,
      showArrows = true,
      snap = true,
      ariaLabelPrev,
      ariaLabelNext,
    },
    ref
  ) => {
    const localRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as any).current = node;
    };

    const [isOverflowing, setIsOverflowing] = useState(false);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const updateState = () => {
      const el = localRef.current;
      if (!el) return;
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      const EPS = 8;
      setIsOverflowing(overflowing);
      const firstChild = el.firstElementChild as HTMLElement | null;
      const lastChild = el.lastElementChild as HTMLElement | null;
      if (firstChild && lastChild) {
        const cRect = el.getBoundingClientRect();
        const fRect = firstChild.getBoundingClientRect();
        const lRect = lastChild.getBoundingClientRect();
        const atStart = fRect.left >= cRect.left - EPS;
        const atEnd = lRect.right <= cRect.right + EPS;
        setCanPrev(!atStart);
        setCanNext(!atEnd);
        return;
      }
      setCanPrev(el.scrollLeft > EPS);
      setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - EPS);
    };

    const handleScroll = () => updateState();

    const scrollByAmount = (amount: number) => {
      const el = localRef.current;
      if (!el) return;
      el.scrollBy({ left: amount, behavior: "smooth" });
    };

    const scrollPrev = () => scrollByAmount(-220);
    const scrollNext = () => scrollByAmount(220);

    useEffect(() => {
      // Ensure we start at the very left before measuring
      const el = localRef.current;
      if (el) {
        el.scrollLeft = 0;
      }
      // measure after paint
      requestAnimationFrame(() => updateState());
      const onResize = () => updateState();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);

    return (
      <div className="relative sm:inline-block sm:py-2">
        <div
          ref={mergedRef}
          onScroll={handleScroll}
          className={cn(
            "flex gap md:gap-4 p-2",
            "overflow-x-auto sm:overflow-visible scroll-smooth",
            snap && "snap-x snap-mandatory",
            "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            "rounded-full border border-ticketon-light-gray-bg",
            contentClassName,
            className
          )}
        >
          {children}
        </div>

        {showArrows && isOverflowing && (
          <>
            {canPrev && (
              <button
                type="button"
                aria-label={ariaLabelPrev}
                onClick={scrollPrev}
                className={cn(
                  "sm:hidden absolute left-5 top-1/2 -translate-y-1/2 z-50",
                  "inline-flex items-center justify-center h-8 w-8 rounded-full bg-ticketon-blue text-white shadow"
                )}
              >
                <LuArrowLeft className="h-4 w-4" />
              </button>
            )}
            {canNext && (
              <button
                type="button"
                aria-label={ariaLabelNext}
                onClick={scrollNext}
                className={cn(
                  "sm:hidden absolute right-5 top-1/2 -translate-y-1/2 z-50",
                  "inline-flex items-center justify-center h-8 w-8 rounded-full bg-ticketon-blue text-white shadow"
                )}
              >
                <LuArrowRight className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>
    );
  }
);

TabsScroller.displayName = "TabsScroller";


